import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router';
import Box from '@amzn/awsui-components-console/box';
import Spinner from '@amzn/awsui-components-console/spinner';
import { useListNamespacesLazyQuery } from 'src/generated/graphql';
import LandingPage from './LandingPage';

/** Probe page size — the maximum the Smithy pagination parameters allow. */
export const PROBE_PAGE_SIZE = 100;

/** Caps the walk so a large, mostly-unpermitted table cannot stall the route. */
export const MAX_PROBE_PAGES = 50;

/**
 * Entry gate for the "/" route. On load it looks for one accessible namespace; if
 * the user has at least one, it redirects to that namespace's page. Otherwise —
 * no namespaces, or the request failed — it renders the landing page so the user
 * is never left on a blank screen.
 *
 * The probe walks `nextToken` until it finds a namespace or the token runs out.
 * `listNamespaces` applies the VIEW permission filter AFTER paging, so an early
 * page can be empty (or short) while later pages still hold accessible
 * namespaces; stopping at the first empty page would strand a permitted user on
 * the landing page.
 *
 * `no-cache` because the probe reads each page once and never re-reads it, so
 * retaining up to `MAX_PROBE_PAGES` pages of namespaces would be pure memory
 * cost.
 */
const LandingEntry: React.FC = () => {
  const [executeListNamespaces] = useListNamespacesLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const [firstNamespaceId, setFirstNamespaceId] = useState<string | null>(null);
  const [probing, setProbing] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const findFirstNamespace = async () => {
      let nextToken: string | undefined = undefined;

      for (let page = 0; page < MAX_PROBE_PAGES; page++) {
        const { data, error } = await executeListNamespaces({
          variables: { pageSize: PROBE_PAGE_SIZE, nextToken },
        });
        if (cancelled) return;

        // A failed page ends the probe; the landing page is the fallback.
        if (error || !data) return;

        const found = data.listNamespaces.namespaces[0];
        if (found) {
          setFirstNamespaceId(found.namespaceId);
          return;
        }

        nextToken = data.listNamespaces.nextToken ?? undefined;
        if (!nextToken) return;
      }
    };

    findFirstNamespace()
      .catch(() => {
        // Same fallback as a failed page — render the landing page.
      })
      .finally(() => {
        if (!cancelled) setProbing(false);
      });

    return () => {
      cancelled = true;
    };
  }, [executeListNamespaces]);

  if (firstNamespaceId) {
    return <Navigate to={`/${firstNamespaceId}`} replace />;
  }

  if (probing) {
    return (
      <Box textAlign="center" padding={{ vertical: 'xxxl' }}>
        <Spinner size="large" />
      </Box>
    );
  }

  return <LandingPage />;
};

export default LandingEntry;
